import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import useCommunityPolicies from '@aurora/shared-client/components/community/useCommunityPolicies';
import {
  canAccessPageBuilder as checkCanAccessPageBuilder,
  canAdminNode,
  canArchiveMessage,
  canManageArticleBlog,
  canManageArticleTkb
} from '@aurora/shared-client/helpers/nodes/NodePolicyHelper';
import { dropdownPopperConfig } from '@aurora/shared-client/helpers/ui/PopperJsHelper';
import Icons from '@aurora/shared-client/icons';
import type { NodePageAndParams } from '@aurora/shared-client/routes/adminRoutes';
import { AdminPages } from '@aurora/shared-client/routes/adminRoutes';
import type { ManageContentPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useAdminUserRoutes from '@aurora/shared-client/routes/useAdminRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { Board } from '@aurora/shared-generated/types/graphql-schema-types';
import type { BoardPoliciesFragment } from '@aurora/shared-generated/types/graphql-types';
import {
  EndUserComponent,
  EndUserPages,
  EndUserQueryParams
} from '@aurora/shared-types/pages/enums';
import { getLog } from '@aurora/shared-utils/log';
import React from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import { BoardAction } from '../../../../types/enums';
import { MessageListTabItem } from '../../../messages/MessageListTabs/MessageListTabs';
import useEndUserPageDescriptor from '../../../useEndUserPageDescriptor';
import { usePageEditorLink } from '../../../usePageEditorLink';
import useTranslation from '../../../useTranslation';
import localStyles from './BoardActionMenu.module.pcss';

const log = getLog(module);

interface BoardActionMenuItem {
  /**
   * Name of the action
   */
  name: BoardAction;
  /**
   * Method to determine whether to display the action item
   * @return whether or not to display the action item.
   */
  policy?: () => boolean;
  /**
   * Event handler for the action taken
   */
  actionHandler: () => void;
}

interface Props {
  /**
   * the Board information object
   */
  board: BoardPoliciesFragment & Pick<Board, 'id' | 'conversationStyle'>;
}

/**
 * Display Board Action Menu
 *
 * Is not displayed when user does not meet policy criteria to see the any of the menu items
 *
 * @author Guido Mininno
 */
const BoardActionMenu: React.FC<React.PropsWithChildren<Props>> = ({ board }) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.BOARD_ACTION_MENU
  );
  const adminRouter = useAdminUserRoutes().router;
  const { router: endUserRouter } = useEndUserRoutes();
  const { isEditable: isPageEditable } = useEndUserPageDescriptor();
  const { link: pageEditorLink } = usePageEditorLink();

  const {
    data: communityPoliciesData,
    loading: communityPoliciesLoading,
    error: communityPoliciesError
  } = useCommunityPolicies(module, {
    useCanAccessPageBuilder: true,
    useCanArchiveMessage: true
  });

  if (textLoading || communityPoliciesLoading) {
    return null;
  }

  if (communityPoliciesError) {
    log.error(`Error fetching policy information: ${communityPoliciesError.message}`);
  }

  const canAccessPageBuilder: boolean = checkCanAccessPageBuilder(communityPoliciesData?.coreNode);

  /**
   * Event handler for Edit Board Settings action
   */
  async function onEditBoardSettingsHandler(): Promise<void> {
    await adminRouter.pushRoute<NodePageAndParams>(AdminPages.NodeSettingsPage, {
      nodeId: board.id
    });
  }

  /**
   * Event handler for Manage Content in Board action
   */
  async function onManageBoardContentHandler(): Promise<void> {
    await endUserRouter.pushRoute<ManageContentPageAndParams>(
      EndUserPages.ManageContentPage,
      null,
      {
        [EndUserQueryParams.SEARCH_FILTER_BY_LOCATION]: board?.id
      }
    );
  }

  /**
   * Event handler that takes care of the navigation to the
   * Manage Content page with the archives tab selected.
   */
  async function onManageArchivesHandler(): Promise<void> {
    await endUserRouter.pushRoute<ManageContentPageAndParams>(
      EndUserPages.ManageContentPage,
      null,
      {
        [EndUserQueryParams.TAB]: MessageListTabItem.ARCHIVES,
        [EndUserQueryParams.SEARCH_FILTER_BY_LOCATION]: board?.id
      }
    );
  }

  const adminNodeActions: BoardActionMenuItem[] = [
    {
      name: BoardAction.MANAGE_BOARD_CONTENT,
      policy: (): boolean => canManageArticleBlog(board) || canManageArticleTkb(board),
      actionHandler: onManageBoardContentHandler
    },
    {
      name: BoardAction.MANAGE_ARCHIVES,
      policy: (): boolean => canArchiveMessage(board),
      actionHandler: onManageArchivesHandler
    },
    {
      name: BoardAction.EDIT_BOARD_SETTINGS,
      policy: (): boolean => canAdminNode(board),
      actionHandler: onEditBoardSettingsHandler
    }
  ];

  /**
   * Function to check if the action item should be displayed
   *
   * @param policy A callback function to determine if action item should be displayed
   * @return boolean whether to display the action item
   * will render if no policy was provided.
   */
  function actionShouldDisplay(policy: () => boolean): boolean {
    if (policy) {
      return policy();
    }
    return true;
  }

  const hasAdminNodeItemsToDisplay: boolean =
    adminNodeActions?.filter(action => actionShouldDisplay(action?.policy)).length > 0;

  /**
   * Renders admin node menu items in Board Action Menu
   *
   * @param action The action to be displayed
   */
  function renderAdminMenuItem(action: BoardActionMenuItem): React.ReactElement {
    const displayAdminMenuItem = actionShouldDisplay(action?.policy);
    const actionHandler = action?.actionHandler;

    if (displayAdminMenuItem) {
      return (
        <Dropdown.Item
          key={action.name}
          onClick={actionHandler}
          className={cx('lia-dropdown-item')}
          data-testid={`BoardActionMenuItem.${action.name}`}
        >
          {formatMessage(`${action.name}.board`)}
        </Dropdown.Item>
      );
    }
    return null;
  }

  /**
   * Renders the edit page link in Group Hub Action Menu
   */
  function renderEditPageLink(): React.ReactElement {
    return pageEditorLink(
      <Dropdown.Item
        key={'editPage'}
        className={cx('lia-dropdown-item')}
        data-testid={'BoardActionMenuItem.EditPage'}
      >
        {formatMessage('editPage')}
      </Dropdown.Item>
    );
  }

  return (
    (hasAdminNodeItemsToDisplay || (isPageEditable && canAccessPageBuilder)) && (
      <>
        <Dropdown
          className={cx('lia-g-ml-10')}
          title={formatMessage('actionMenu.title')}
          aria-label={formatMessage('actionMenu.ariaLabel')}
        >
          <Dropdown.Toggle
            id="lia-board-action-menu"
            as={Button}
            aria-label={formatMessage('toggleButtonLabel')}
            className={cx('lia-g-icon-btn')}
            variant={ButtonVariant.NO_VARIANT}
          >
            <Icon
              icon={Icons.GearIcon}
              size={IconSize.PX_20}
              color={IconColor.GRAY_900}
              testId="BoardActionMenuItem.GearIcon"
            />
          </Dropdown.Toggle>
          <Dropdown.Menu
            align="right"
            aria-labelledby="lia-board-action-menu"
            className={cx('lia-dropdown-menu lia-dropdown-menu-index')}
            popperConfig={dropdownPopperConfig}
            renderOnMount
            data-testid="BoardActionMenuItem.dropdown"
          >
            {hasAdminNodeItemsToDisplay &&
              adminNodeActions.map(action => renderAdminMenuItem(action))}
            {isPageEditable && canAccessPageBuilder && renderEditPageLink()}
          </Dropdown.Menu>
        </Dropdown>
      </>
    )
  );
};

export default BoardActionMenu;
